import { XIcon } from '@phosphor-icons/react';
import { type CSSProperties, useEffect, useRef } from 'react';

import { useContent } from '@/content/ContentContext';
import { ButtonLink } from '@/ui/Button/Button';

import styles from './MobileMenu.module.css';

interface MobileMenuProps {
  readonly id: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

/** Menu plein écran pour mobile : fermeture par Échap, focus contenu dans le menu. */
export function MobileMenu({ id, open, onClose }: MobileMenuProps) {
  const { navigation } = useContent();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;

    const focusables = (): HTMLElement[] =>
      Array.from(dialog.querySelectorAll<HTMLElement>('a[href], button'));
    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  return (
    <div
      ref={dialogRef}
      id={id}
      className={styles.menu}
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      data-open={open || undefined}
      inert={!open}
    >
      <div className={styles.top}>
        <button
          type="button"
          className={styles.close}
          aria-label={navigation.closeMenu}
          onClick={onClose}
        >
          <XIcon size={20} aria-hidden="true" />
        </button>
      </div>
      <nav className={styles.links} aria-label="Navigation mobile">
        {navigation.links.map((link, index) => (
          <a
            key={link.href}
            className={styles.link}
            href={link.href}
            style={{ '--index': index } as CSSProperties}
            onClick={onClose}
          >
            {link.label}
          </a>
        ))}
      </nav>
      <ButtonLink className={styles.cta} href={navigation.cta.href} onClick={onClose}>
        {navigation.cta.label}
      </ButtonLink>
    </div>
  );
}
